import React, { useCallback, useState } from 'react';
import {
  CardContent, Divider,
  InputLabel, Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import CustomTextField from '../../../../../components/CustomTextField';
import { Trash } from '../../../../../icons/trash';
import { InsightLabelItem } from '../../../../../components/common/InsightLabelItem';
import STRING_KEYS from '../../../../../language/keys';
import { InstitutionRow } from '../../InstitutionRow';
import CompanyAutocomplete from '../../CompanyAutocomplete';
import { useGetCompaniesQuery } from '../../../../../hooks/useTalentCompany';
import { DateItem } from '../../DateItem';
import { uploloadCompanyImage } from '../../../../../api/talentsApi';
import useDebounce from '../../../../../hooks/useDebounce';
import { ErrorMessageSnackbar } from '../../../../../components/ErrorMessageSnackbar';
import { CompanyFallback } from '../../../../../components/common/avatars/CompanyFallback';
import CopyableLabel from '../../../../../components/CopyableLabel';
import { SNACK_BAR_CLOSE_REASON } from '../../utils/consts';
import { VIEW_MODE } from '../../../../../consts/enums';
import CustomCheckbox from '../../../../../components/CustomCheckbox';
import { style } from './style';

export type OptionAutocomplete = {
  id: string,
  logo: string,
  name: string,
  description: string
}

type TalentExperienceItemProps = {
  companyName: string,
  companyLogo: string,
  id: string,
  role: string,
  onDataValueChanged: (select: UpdateValues<string | boolean | null | DateObject>,
    id: string) => void,
  badges: BadgeItem[],
  onDeleteBlock?: (id:string) => void,
  placeholderTitle: string,
  companyId: string,
  disabled?: boolean,
  startDate: DateObject,
  endDate: DateObject | null,
  onOpenCreateNewCompanyModal: ((id:string) => void) | undefined,
  viewMode: ViewMode
  keyInsights?: string;
}

const TalentExperienceItem = ({
  companyLogo,
  companyName,
  onDataValueChanged,
  id,
  role,
  badges,
  onDeleteBlock,
  placeholderTitle,
  companyId,
  disabled = false,
  startDate,
  endDate,
  onOpenCreateNewCompanyModal,
  viewMode,
  keyInsights,
}: TalentExperienceItemProps):JSX.Element => {
  const { t: translate } = useTranslation();
  const [companyChange, setCompanyChange] = useState<string>('');
  const { debouncedValue: companiesDebounce, isDebouncing } = useDebounce(companyChange);
  const [companiesGetFrom, setCompaniesGetFrom] = useState<string>('');
  const { companies, isLoading }  = useGetCompaniesQuery(
    companiesDebounce,
    (type) => setCompaniesGetFrom(type));
  const [isChangedCurrent, setIsChangedCurrent] = useState<boolean>(!endDate);
  const [isUploading, setIsUploadLoading] = useState<boolean>(false);
  const [errorUploadImage, setErrorUploadImage] = useState<
  {error: boolean, errorMessage: string, }>({
    error: false,
    errorMessage: '',
  });
  const onOptionSelected = (event:React.SyntheticEvent, selectedValues:Company | null) => {
    if (!selectedValues) return;

    onDataValueChanged({
      companyLogo: selectedValues.logo,
      companyId: selectedValues.id,
      companyName: selectedValues.name,
    }, id);
  };

  const onChangeValues = (e:React.SyntheticEvent, newVal:string) => {
    onDataValueChanged({
      companyName: newVal,
      companyId: '',
      companyLogo: '',
    }, id);
  };

  const handleCompanyChange = (event:React.SyntheticEvent, newInputValue:string) => {
    setCompanyChange(newInputValue);
  };

  const handleInputUploadImage = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const myFile = e.target.files ? e.target.files[0] : '';
    const formData = new FormData();
    formData.append('file', myFile);
    setIsUploadLoading(true);
    uploloadCompanyImage(formData).then((data: { fileUrl: string }) => {
      onDataValueChanged({ companyLogo: data.fileUrl }, id);
    })
      .catch((error) => {
        setErrorUploadImage({
          error: true,
          errorMessage: error.message,
        });
      })
      .finally(() => {
        setIsUploadLoading(false);
      });
  }, [setIsUploadLoading, setErrorUploadImage, id, onDataValueChanged]);

  const handleSnackbarClose = (e: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === SNACK_BAR_CLOSE_REASON.CLICK_AWAY) {
      return;
    }
    setErrorUploadImage({ error: false, errorMessage: '' });
  };

  const canDelete = typeof onDeleteBlock === 'function' && viewMode === VIEW_MODE.EDITABLE;
  const fieldModeStyle =
    viewMode === VIEW_MODE.READ_ONLY ? {} :  style.card.CardContent.dateBlock.boxInner;

  return (
    <>
      {
        errorUploadImage.error && (
          <ErrorMessageSnackbar
            showError={  errorUploadImage.error }
            onClose={ handleSnackbarClose }
            errorMessage={ errorUploadImage.errorMessage }
          />
        )
      }
      <CardContent sx={ style.card.CardContent.cardContentContainer.sx }>
        <InstitutionRow
          isloading={ isUploading }
          logo={ companyLogo }
          onChangeImage={ handleInputUploadImage }
          canUploadImage={ companyId === '' }
          disabled
          FallBack={ CompanyFallback }
          viewMode={ viewMode }
        >
          <Box sx={ style.autoCompleteBox.sx } style={ style.autoCompleteBox }>
            <CompanyAutocomplete
              id={ id }
              companiesGetFrom={ companiesGetFrom }
              listAutocomplete={ companies }
              onInputValueChange={ handleCompanyChange }
              onOptionSelected={ onOptionSelected }
              onChangeValues={ onChangeValues }
              valueName={ companyName || '' }
              label={ translate(STRING_KEYS.COMPANY) }
              disabled={ disabled }
              isLoading={ isLoading || isDebouncing }
              onOpenCreateNewCompanyModal={ onOpenCreateNewCompanyModal }
              viewMode={ viewMode }
            />
            {companyId && (
              <CopyableLabel labelText={ translate(STRING_KEYS.ID, { id: companyId }) }
                dataToCopy={ companyId }
                variant="caption"
                maxWidth="12rem"
                textColor="text.secondary"
              />
            )}
          </Box>
        </InstitutionRow>
        <Box >
          <CustomTextField
            viewMode={ viewMode }
            value={ role }
            name="title"
            onChange={ (e:React.ChangeEvent<HTMLInputElement>) => {
              onDataValueChanged({ [e.target.name]: e.target.value }, id);
            } }
            label={ placeholderTitle }
            fullWidth
            disabled={ disabled }
          />
        </Box>
        <Box style={ style.card.CardContent.dateBlock.box }>
          {/* box start */}
          <Box sx={ style.card.CardContent.dateBlock.boxMain }>
            <Box sx={ fieldModeStyle }>
              <Box>
                <Typography
                  width={ style.card.CardContent.dateBlock.typography.width }
                  variant={ style.card.CardContent.dateBlock.typography.variant }
                  sx={ style.card.CardContent.dateBlock.typography.sx }
                >
                  { translate(STRING_KEYS.START_DATE_TITLE) }
                </Typography>
                <DateItem
                  companyId={ id }
                  onDateValueChanged={ onDataValueChanged }
                  name="startDate"
                  disabled={ false }
                  dateObj={ startDate }
                  viewMode={ viewMode }
                />
              </Box>
            </Box>
            {/* box end */}
            <Box sx={ fieldModeStyle }>
              <Box>
                <Typography
                  width={ style.card.CardContent.dateBlock.typography.width }
                  variant={ style.card.CardContent.dateBlock.typography.variant }
                  sx={ style.card.CardContent.dateBlock.typography.isDisabled(isChangedCurrent) }
                >
                  { translate(STRING_KEYS.END_DATE_TITLE) }
                </Typography>
                <Box sx={ style.card.CardContent.dateBlock.boxMain }>
                  <Box sx={ style.card.CardContent.textField.field }>
                    <DateItem
                      companyId={ id }
                      onDateValueChanged={ onDataValueChanged }
                      // title={ translate(STRING_KEYS.END_DATE_TITLE) }
                      name="endDate"
                      disabled={ isChangedCurrent }
                      dateObj={ endDate }
                      viewMode={ viewMode }
                    />
                  </Box>
                  <InputLabel
                    style={ style.card.CardContent.dateBlock.chackboxLabel }
                    htmlFor="current"
                  >
                    <CustomCheckbox
                      viewMode={ viewMode }
                      name="isCurrent"
                      checked={ isChangedCurrent }
                      disabled={ disabled }
                      onChange={ () => {
                        onDataValueChanged({ endDate: null }, id);
                        setIsChangedCurrent(!isChangedCurrent);
                      } }
                    />
                    { translate(STRING_KEYS.CURRENT) }
                  </InputLabel>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* label block */}
        <Typography
          style={ style.card.CardContent.label.style }
          sx={ style.card.CardContent.label.sx  }
        >{translate(STRING_KEYS.INSIGHT_LABEL)}
        </Typography>
        <InsightLabelItem
          badges={  badges }
          onDateValueChanged={ onDataValueChanged }
          companyId={ id }
          disabled={ disabled }
          showClearButton={ !disabled }
          viewMode={ viewMode }
        />

        <Typography
          style={ style.card.CardContent.label.style }
          sx={ style.card.CardContent.label.sx  }
        >{translate(STRING_KEYS.INSIGHT_KEY)}
        </Typography>

        <CustomTextField
          placeholder={ translate(STRING_KEYS.INSIGHT_KEY) }
          fullWidth
          value={ keyInsights }
          onChange={ (e:React.ChangeEvent<HTMLInputElement>) => {
            onDataValueChanged({ keyInsights: e?.target?.value }, id);
          } }
          disabled={ disabled }
          viewMode={ viewMode }
        />
        {/* trash block */}
        {canDelete && (
          <>
            <Divider />
            <Box onClick={ () => onDeleteBlock(id) }
              sx={ style.card.CardContent.boxTrash.sx }
            >
              <Trash color={ style.card.CardContent.boxTrash.color } />
            </Box>
          </>

        )}
      </CardContent>
      <Divider />
    </>
  );
};

TalentExperienceItem.defaultProps = {
  keyInsights: '',
  onDeleteBlock: undefined,
  disabled: false,
};

export default React.memo(TalentExperienceItem);
